import { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import get from 'lodash/get';
import { useDebouncedCallback } from 'use-debounce';
import Yup from 'common/components/CopyMagic/shared/configureYup';
import copyMagicLog from 'common/components/CopyMagic/shared/copyMagicLog';
import { useResultContext } from 'common/components/CopyMagic/contexts';

interface ValidateInputCallbacks {
  successCallback: () => void;
  errorCallback: (message: string) => void; // eslint-disable-line no-unused-vars
}

const INPUT_ID_PREFIX = 'copy-magic-input';

const validateTextInput = (
  el: HTMLInputElement,
  validationSchema: Yup.StringSchema,
  { successCallback, errorCallback }: ValidateInputCallbacks,
) => {
  try {
    validationSchema.validateSync(el.value);
    successCallback();
  } catch (err) {
    if (err instanceof Error) {
      errorCallback(err.message);
    } else {
      copyMagicLog(err, { level: 'error' });
    }
  }
};

const useEditResultItem = ({
  attributePath,
  validationSchema,
}: {
  attributePath: (string | number)[];
  validationSchema: Yup.StringSchema;
}) => {
  const { result, setResultValue } = useResultContext();
  const valueInitial = useMemo(() => get(result, attributePath, ''), [result, attributePath]);
  const inputName = useMemo(() => [INPUT_ID_PREFIX, ...attributePath].join('-'), [attributePath]);

  const [value, setValue] = useState(valueInitial);
  const resetValue = useCallback(() => setValue(valueInitial), [valueInitial]);

  const [errorMessage, setErrorMessage] = useState('');
  const resetErrorMessage = () => setErrorMessage('');

  /*
    If the user quickly creates a validation error and blurs the input, we continue to display
    the error message via a resetTimer.
    - OnBlur with error: resetTimer is started
    - OnFocus: resetTimer is cancelled
    - Component unmount: resetTimer is cancelled
    - ResetTimer finish: input is reset to initial value and error message removed
  */
  const resetTimer: React.MutableRefObject<undefined | ReturnType<typeof setTimeout>> = useRef();
  const startResetTimer = useCallback(() => {
    resetTimer.current = setTimeout(() => {
      resetValue();
      resetErrorMessage();
    }, 3000);
  }, [resetValue]);
  const cancelResetTimer = () => clearTimeout(resetTimer.current);
  useEffect(() => () => cancelResetTimer(), []);

  const debouncedValidateTextInput = useDebouncedCallback(
    (_el, _schema, _callbacks: ValidateInputCallbacks) => {
      validateTextInput(_el, _schema, _callbacks);
    },
    500,
  );

  const onChangeHandler = useCallback(
    (evt: React.ChangeEvent<HTMLInputElement>) => {
      setValue(evt.target.value);
      debouncedValidateTextInput(evt.target, validationSchema, {
        successCallback: () => resetErrorMessage(),
        errorCallback: (message) => setErrorMessage(message),
      });
    },
    [debouncedValidateTextInput, validationSchema],
  );

  const onFocusHandler = useCallback(cancelResetTimer, [cancelResetTimer]);

  const onBlurHandler = useCallback(
    (evt: React.ChangeEvent<HTMLInputElement>) => {
      if (valueInitial === evt.target.value) return;

      validateTextInput(evt.target, validationSchema, {
        successCallback: () => {
          setResultValue(attributePath, evt.target.value);
          resetErrorMessage();
        },
        errorCallback: (message) => {
          setErrorMessage(message);
          startResetTimer();
        },
      });
    },
    [valueInitial, validationSchema, setResultValue, attributePath, startResetTimer],
  );

  return {
    value,
    errorMessage,
    onChangeHandler,
    onFocusHandler,
    onBlurHandler,
    inputName,
  };
};

export default useEditResultItem;
